import {ChangeDetectionStrategy, Component, computed, effect, inject, input} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, catchError, map, Observable, of, startWith, switchMap, tap} from 'rxjs';
import {LoadComponent, ModalInputService, PopoverIconComponent} from '@brunata-angular/ui';
import {TranslatePipe} from '@brunata-angular/util';
import {SftpConnectionsService} from '../../sftp-connections/sftp-connections.service';
import {SftpConnection} from '../../sftp-connections/sftp-connection.model';
import {
    SftpConnectionModalComponent,
    sftpConnectionModalOptions,
} from '../../sftp-connections/sftp-connection-modal/sftp-connection-modal.component';
import {maxSavedExportPathLength} from '../../saved-exports/saved-export.model';
import {ExportScheduleForm, scheduleFormValue} from '../../shared/models/export-form.model';

@Component({
    selector: 'app-sftp-delivery-panel',
    standalone: true,
    imports: [LoadComponent, PopoverIconComponent, ReactiveFormsModule, TranslatePipe],
    template: `
        @if (loaded() === null) {
            <app-load fadeSpeed="FAST" />
        } @else if (loadFailed()) {
            <div class="alert alert-danger d-flex align-items-start gap-2">
                <i class="bi bi-exclamation-triangle-fill flex-shrink-0" aria-hidden="true"></i>
                <div>
                    {{ 'savedexport_sftp_connections_load_error' | translate: 'online' : 'export' }}
                </div>
            </div>
        } @else if (connections().length === 0) {
            <div class="alert alert-web-blue d-flex align-items-start gap-2">
                <i class="bi bi-info-circle-fill flex-shrink-0" aria-hidden="true"></i>
                <div>
                    <p class="mb-2">
                        {{ 'savedexport_sftp_no_connections' | translate: 'online' : 'export' }}
                    </p>
                    <button
                        type="button"
                        class="btn btn-outline-primary btn-sm text-nowrap"
                        (click)="createConnection()">
                        {{ 'sftpconnection_overview_create' | translate: 'online' : 'export' }}
                    </button>
                </div>
            </div>
        } @else {
            <div class="row g-4">
                <div class="col-12 col-lg-7">
                    <div class="d-flex justify-content-between align-items-center gap-2 mb-1">
                        <label class="form-label required mb-0" for="sftp-connection">
                            {{
                                'savedexport_sftp_connection_label' | translate: 'online' : 'export'
                            }}
                        </label>
                        <button
                            type="button"
                            class="btn btn-outline-primary btn-sm text-nowrap"
                            (click)="editSelectedConnection()">
                            {{
                                'savedexport_sftp_manage_connections'
                                    | translate: 'online' : 'export'
                            }}
                        </button>
                    </div>
                    <select
                        class="form-select"
                        id="sftp-connection"
                        [class.is-invalid]="connectionMissing()"
                        [formControl]="schedule().controls.sftpConnectionId">
                        @for (connection of connections(); track connection.sftpConnectionId) {
                            <option [value]="connection.sftpConnectionId">
                                {{ connection.name }}
                            </option>
                        }
                    </select>

                    <div class="form-label mt-4">
                        {{ 'savedexport_sftp_placement_heading' | translate: 'online' : 'export' }}
                    </div>
                    <div class="d-flex justify-content-between">
                        <label class="form-label" for="sftp-subfolder">
                            {{
                                'savedexport_sftp_subfolder_label' | translate: 'online' : 'export'
                            }}
                            <span class="text-secondary">
                                {{
                                    'savedexport_sftp_subfolder_optional'
                                        | translate: 'online' : 'export'
                                }}
                            </span>
                        </label>
                        <app-popover-icon
                            class="ms-3"
                            [popover]="
                                'savedexport_sftp_subfolder_hint'
                                    | translate
                                        : 'online'
                                        : 'export'
                                        : [baseFolder(), connectionName()]
                            " />
                    </div>
                    <div class="input-group">
                        <span class="input-group-text font-monospace">{{ baseFolder() }}</span>
                        <input
                            type="text"
                            class="form-control font-monospace"
                            id="sftp-subfolder"
                            [maxlength]="maxPathLength"
                            [formControl]="schedule().controls.sftpSubfolder" />
                    </div>
                    <div class="d-flex justify-content-between mt-3">
                        <label class="form-label" for="sftp-filename">
                            {{ 'savedexport_sftp_filename_label' | translate: 'online' : 'export' }}
                        </label>
                        <app-popover-icon
                            class="ms-3"
                            [popover]="
                                'savedexport_sftp_filename_hint' | translate: 'online' : 'export'
                            " />
                    </div>
                    <input
                        type="text"
                        class="form-control font-monospace"
                        id="sftp-filename"
                        [maxlength]="maxPathLength"
                        [formControl]="schedule().controls.fileNamePattern" />
                    @if (fileName(); as fileName) {
                        <div class="form-text">
                            <code class="text-break">{{ fileName }}</code>
                        </div>
                    }

                    <div class="d-flex justify-content-between mt-4">
                        <label class="form-label" for="sftp-test-path">
                            {{
                                'savedexport_sftp_test_placement_label'
                                    | translate: 'online' : 'export'
                            }}
                        </label>
                        <app-popover-icon
                            class="ms-3"
                            [popover]="
                                'savedexport_sftp_test_placement_hint'
                                    | translate: 'online' : 'export'
                            " />
                    </div>
                    <input
                        type="text"
                        class="form-control font-monospace bg-body-tertiary"
                        id="sftp-test-path"
                        readonly
                        [value]="testPath()" />
                </div>

                <div class="col-12 col-lg-5">
                    <div class="alert alert-web-blue d-flex align-items-start gap-2">
                        <i class="bi bi-info-circle-fill flex-shrink-0" aria-hidden="true"></i>
                        <div>
                            {{
                                'savedexport_sftp_connection_info' | translate: 'online' : 'export'
                            }}
                        </div>
                    </div>
                </div>
            </div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SftpDeliveryPanelComponent {
    readonly schedule = input.required<ExportScheduleForm>();
    readonly fileName = input<string | null>(null);

    private readonly sftpConnectionsService = inject(SftpConnectionsService);
    private readonly modalInputService = inject(ModalInputService);

    private readonly reload$ = new BehaviorSubject<void>(undefined);

    protected readonly loaded = toSignal(
        this.reload$.pipe(
            switchMap(() =>
                this.sftpConnectionsService.getSftpConnections().pipe(
                    map(page => ({connections: page.sftpConnections, failed: false})),
                    catchError(() => of({connections: [], failed: true})),
                ),
            ),
        ),
        {initialValue: null},
    );

    protected readonly connections = computed(() => this.loaded()?.connections ?? []);

    protected readonly loadFailed = computed(() => this.loaded()?.failed === true);

    private readonly value = scheduleFormValue(this.schedule);

    protected readonly selected = computed(() =>
        this.connections().find(
            connection => connection.sftpConnectionId === this.value()?.sftpConnectionId,
        ),
    );

    private readonly connectionTouched = toSignal(
        toObservable(this.schedule).pipe(
            switchMap(form =>
                form.controls.sftpConnectionId.events.pipe(
                    startWith(null),
                    map(() => form.controls.sftpConnectionId.touched),
                ),
            ),
        ),
        {initialValue: false},
    );

    protected readonly connectionMissing = computed(
        () =>
            this.loaded() !== null &&
            this.selected() === undefined &&
            (this.value()?.sftpConnectionId != null || this.connectionTouched()),
    );

    private readonly latestUsed = computed(() =>
        [...this.connections()]
            .sort((a, b) => (b.latestSavedExportId ?? 0) - (a.latestSavedExportId ?? 0))
            .at(0),
    );

    protected readonly baseFolder = computed(() => this.selected()?.baseFolder ?? '');

    protected readonly connectionName = computed(() => this.selected()?.name ?? '');

    protected readonly testPath = computed(() => this.selected()?.testPath ?? '');

    protected readonly maxPathLength = maxSavedExportPathLength;

    private readonly storedConnectionDeleted = computed(
        () =>
            this.loaded()?.failed === false &&
            this.selected() === undefined &&
            this.value()?.sftpConnectionId != null,
    );

    constructor() {
        effect(() => {
            const control = this.schedule().controls.sftpConnectionId;
            if (this.storedConnectionDeleted() && !control.dirty) {
                control.setValue(null);
                control.markAsTouched();
            }
        });
        // A first-time SFTP export opens on the connection used last; a deleted one stays empty
        // so the user has to choose its replacement.
        effect(() => {
            const latestUsed = this.latestUsed();
            if (
                latestUsed === undefined ||
                this.selected() !== undefined ||
                this.connectionMissing()
            ) {
                return;
            }
            this.schedule().controls.sftpConnectionId.setValue(latestUsed.sftpConnectionId);
        });
    }

    protected createConnection(): void {
        this.openConnectionModal(null).subscribe(created => {
            const control = this.schedule().controls.sftpConnectionId;
            control.setValue(created.sftpConnectionId);
            control.markAsDirty();
        });
    }

    protected editSelectedConnection(): void {
        this.openConnectionModal(this.selected() ?? null).subscribe();
    }

    private openConnectionModal(sftpConnection: SftpConnection | null): Observable<SftpConnection> {
        return this.modalInputService
            .open(SftpConnectionModalComponent, {sftpConnection}, sftpConnectionModalOptions)
            .closed$.pipe(tap(() => this.reload$.next()));
    }
}
